import { useState, useContext, useEffect, useRef } from "react";
import { fetchedPreSalesByAction, fetchedUsersByGang } from "../../../apis/apis.dispatches/apisDispatch";
import { fetchedGangs } from "../../../apis/apis.logistics/apisProduct";
import { getCurrentDate } from "../../../configs/helpers";
import { toast } from "react-toastify";
import { ContextAuth } from "../../../contexts/ContextAuth";

const inputCls = "w-full rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-colors";
const labelCls = "block text-xs font-semibold text-gray-600 dark:text-gray-400 uppercase tracking-wide mb-1";

export function OrderTrayFilter({
    setOrders, gangs, setGangs, users, setUsers,
    dailyRouteStatus, setFilename, ordersCount, registerSearchFn,
}) {
    const { auth } = useContext(ContextAuth);
    const [questionState, setQuestionState] = useState({
        startDate:        getCurrentDate("-"),
        endDate:          getCurrentDate("-"),
        subsidiaryID:     auth.data.subsidiary.subsidiary_id,
        warehouseID:      0,
        gangID:           0,
        userID:           0,
        dailyRouteStatus: "04",
        includeDates:     false,
    });
    const questionStateRef = useRef(questionState);
    questionStateRef.current = questionState;

    const getGangs = async () => {
        try { setGangs(await fetchedGangs()); }
        catch (e) { throw e; }
    };
    const getUsersByGang = async (id) => {
        try { setUsers(await fetchedUsersByGang({ gangID: id })); }
        catch (e) { throw e; }
    };

    const searchSuggestions = () => {
        fetchedPreSalesByAction(questionStateRef.current).then((res) => {
            setOrders(res);
            const n = res.length;
            toast.info(n > 1 ? `Se encontraron ${n} resultados.` : `Se encontró ${n} resultado(s).`);
        });
    };

    useEffect(() => {
        registerSearchFn?.(() => searchSuggestions);
    }, []);

    useEffect(() => { getGangs(); }, []);

    function handleChange(e) {
        const updated = { ...questionState, [e.target.name]: e.target.value };
        setQuestionState(updated);
        if (e.target.name === "gangID") {
            const select = document.getElementById("gangID");
            const text   = select.options[select.selectedIndex].text;
            setFilename(`ReporteVentas_${text.replace(/\s/g, "")}_${getCurrentDate("-")}`);
            getUsersByGang(e.target.value);
        }
    }

    return (
        <div className="rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 shadow-sm">
            <div className="flex items-center justify-between px-5 py-4 border-b border-gray-200 dark:border-gray-700">
                <h2 className="text-sm font-semibold text-gray-900 dark:text-white flex items-center gap-2">
                    <i className="fas fa-filter text-blue-500"></i>
                    Filtros de búsqueda
                </h2>
                <span className="inline-flex items-center gap-1.5 rounded-full bg-gray-100 dark:bg-gray-700 px-3 py-1 text-xs font-medium text-gray-600 dark:text-gray-300">
                    <span className="h-1.5 w-1.5 rounded-full bg-blue-500"></span>
                    {ordersCount} registros
                </span>
            </div>

            <div className="px-5 py-4 space-y-4">
                {/* Row 1 */}
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                    <div>
                        <label htmlFor="gangID" className={labelCls}><i className="fas fa-route mr-1"></i>Ruta</label>
                        <select id="gangID" name="gangID" value={questionState.gangID} onChange={handleChange} className={inputCls}>
                            <option value={0}>Seleccionar ruta...</option>
                            {gangs.map((item, i) => (
                                <option key={i} value={item.gangID}>{item.gangName} | {item.warehouseName}</option>
                            ))}
                        </select>
                    </div>
                    <div>
                        <label className={labelCls}><i className="fas fa-user mr-1"></i>Usuario</label>
                        <select name="userID" value={questionState.userID} onChange={(e) => setQuestionState({ ...questionState, userID: e.target.value })} className={inputCls}>
                            <option value={0}>Todos</option>
                            {users.map((item, i) => (
                                <option key={i} value={item.id}>{item.first_name} | {item.last_name}</option>
                            ))}
                        </select>
                    </div>
                    <div>
                        <label className={labelCls}><i className="fas fa-truck mr-1"></i>Estado de entrega</label>
                        <select name="dailyRouteStatus" value={questionState.dailyRouteStatus} onChange={(e) => setQuestionState({ ...questionState, dailyRouteStatus: e.target.value })} className={inputCls}>
                            {dailyRouteStatus.map((item, i) => (
                                <option key={i} value={item.id}>{item.name}</option>
                            ))}
                        </select>
                    </div>
                    {/* Buscar */}
                    <div className="flex items-end">
                        <button
                            id="btn-filter"
                            onClick={searchSuggestions}
                            className="w-full inline-flex items-center justify-center gap-1.5 rounded-lg bg-blue-600 hover:bg-blue-700 active:bg-blue-800 text-white text-sm font-medium px-4 py-2 transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500"
                        >
                            <i className="fas fa-search"></i>Buscar
                        </button>
                    </div>
                </div>

                {/* Row 2: dates + toggle */}
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 items-end">
                    <div>
                        <label className={`${labelCls} ${!questionState.includeDates ? "opacity-40" : ""}`}>
                            <i className="fas fa-calendar mr-1"></i>Desde
                        </label>
                        <input
                            type="date" id="startDate" name="startDate"
                            value={questionState.startDate} onChange={handleChange}
                            disabled={!questionState.includeDates}
                            className={`${inputCls} ${!questionState.includeDates ? "opacity-40 cursor-not-allowed" : ""}`}
                        />
                    </div>
                    <div>
                        <label className={`${labelCls} ${!questionState.includeDates ? "opacity-40" : ""}`}>
                            <i className="fas fa-calendar mr-1"></i>Hasta
                        </label>
                        <input
                            type="date" id="endDate" name="endDate"
                            value={questionState.endDate} onChange={handleChange}
                            disabled={!questionState.includeDates}
                            className={`${inputCls} ${!questionState.includeDates ? "opacity-40 cursor-not-allowed" : ""}`}
                        />
                    </div>
                    <div className="flex items-center gap-2 pb-1">
                        <button
                            type="button"
                            role="switch"
                            aria-checked={questionState.includeDates}
                            onClick={() => setQuestionState({ ...questionState, includeDates: !questionState.includeDates })}
                            className={`relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 ${questionState.includeDates ? "bg-blue-600" : "bg-gray-300 dark:bg-gray-600"}`}
                        >
                            <span className={`inline-block h-5 w-5 transform rounded-full bg-white shadow transition-transform ${questionState.includeDates ? "translate-x-5" : "translate-x-0"}`} />
                        </button>
                        <span className="text-xs font-medium text-gray-700 dark:text-gray-300">
                            Incluir filtro de fecha
                        </span>
                    </div>
                </div>
            </div>
        </div>
    );
}
